import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  FormLabel,
  CircularProgress,
} from "@mui/material";
import { colors } from "../../constants/Colors";
import useIsMobile from "../../hooks/useIsMobile";
import { CompanyType } from "../../types/generalTypes";
import {
  updateCompanyColors,
  updateCompanyLogo,
  getCompanyById,
} from "../../api/CompanyAPI";
import { getCompanyData } from "../../utils/generalUtil";
import {
  uploadCompanyLogo,
  getCompanyLogoByUrl,
} from "../../utils/firebaseStorage";
import FileUpload from "../fileUpload/FileUpload";

interface BrandingStepProps {
  onComplete: (company: CompanyType) => void;
  onError: (message: string) => void;
  onSuccess: (message: string) => void;
}

const BrandingStep = ({
  onComplete,
  onError,
  onSuccess,
}: BrandingStepProps) => {
  const isMobile = useIsMobile();
  const [primaryColor, setPrimaryColor] = useState("#1976d2");
  const [secondaryColor, setSecondaryColor] = useState("#dc004e");
  const [loading, setLoading] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoUrl, setLogoUrl] = useState<string>("");

  useEffect(() => {
    const loadExistingLogo = async () => {
      const companyData = getCompanyData();
      if (companyData.companyId) {
        try {
          const existingLogoUrl = await getCompanyLogoByUrl(
            null,
            companyData.companyId
          );
          if (existingLogoUrl) {
            setLogoUrl(existingLogoUrl);
          }
        } catch (error) {
          console.log("Nenhum logo encontrado para a empresa");
        }
      }
    };

    loadExistingLogo();
  }, []);

  const handleUploadSuccess = (url: string) => {
    setLogoUrl(url);
    setLogoFile(null);
    onSuccess("Logo atualizado com sucesso!");
  };

  const handleUploadError = (error: string) => {
    onError(error);
  };

  useEffect(() => {
    return () => {
      if (logoUrl && logoUrl.startsWith("blob:")) {
        URL.revokeObjectURL(logoUrl);
      }
    };
  }, [logoUrl]);

  const handleSaveBranding = async () => {
    if (!primaryColor || !secondaryColor) {
      onError("Selecione as cores da sua empresa");
      return;
    }

    setLoading(true);
    try {
      const companyData = getCompanyData();

      const colorsResult = await updateCompanyColors(
        companyData.companyId || 0,
        primaryColor,
        secondaryColor
      );

      if (!colorsResult.success) {
        onError(
          colorsResult.message ||
            "Erro ao salvar cores da empresa. Tente novamente."
        );
        return;
      }

      if (logoFile) {
        try {
          const uploadedUrl = await uploadCompanyLogo(
            logoFile,
            companyData.companyId || 0
          );
          const logoResult = await updateCompanyLogo(
            companyData.companyId || 0,
            uploadedUrl
          );

          if (!logoResult.success) {
            onError(logoResult.message || "Erro ao salvar logo da empresa.");
            return;
          }

          setLogoUrl(uploadedUrl);
        } catch (error) {
          onError("Erro ao fazer upload do logo. Tente novamente.");
          return;
        }
      }

      onSuccess("Configurações da empresa salvas com sucesso!");
      const updatedCompany = await getCompanyById(
        companyData?.companyId?.toString() ?? ""
      );
      if (updatedCompany) {
        onComplete(updatedCompany);
      }
    } catch (error) {
      onError("Erro ao salvar configurações da empresa. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography
        sx={{
          fontSize: isMobile ? "14px" : "16px",
          color: "#666",
          marginBottom: isMobile ? "8px" : "16px",
        }}
      >
        Personalize a marca da sua empresa
      </Typography>

      <Typography
        sx={{
          fontSize: isMobile ? "14px" : "16px",
          fontWeight: 600,
          color: "#333",
          marginBottom: isMobile ? "8px" : "12px",
        }}
      >
        Logo da empresa
      </Typography>

      <Box sx={{ marginBottom: isMobile ? "20px" : "24px" }}>
        <FileUpload
          onUploadSuccess={handleUploadSuccess}
          onUploadError={handleUploadError}
          buttonText="Upload Logo"
        />
      </Box>

      <Typography
        sx={{
          fontSize: isMobile ? "14px" : "16px",
          fontWeight: 600,
          color: "#333",
          marginBottom: isMobile ? "8px" : "12px",
        }}
      >
        Cores da empresa
      </Typography>

      <FormControl fullWidth sx={{ marginBottom: isMobile ? "12px" : "16px" }}>
        <FormLabel sx={{ fontSize: isMobile ? "14px" : "16px" }}>
          Cor Primária
        </FormLabel>
        <Box
          sx={{
            display: "flex",
            gap: isMobile ? "6px" : "8px",
            flexDirection: isMobile ? "column" : "row",
            alignItems: isMobile ? "stretch" : "center",
          }}
        >
          <input
            type="color"
            value={primaryColor}
            onChange={(e) => setPrimaryColor(e.target.value)}
            style={{
              width: isMobile ? "100%" : "50px",
              height: isMobile ? "48px" : "40px",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          />
          <TextField
            value={primaryColor}
            onChange={(e) => setPrimaryColor(e.target.value)}
            placeholder="#1976d2"
            variant="outlined"
            disabled={loading}
            sx={{ flex: 1 }}
            size={isMobile ? "small" : "medium"}
          />
        </Box>
      </FormControl>

      <FormControl fullWidth sx={{ marginBottom: isMobile ? "12px" : "16px" }}>
        <FormLabel sx={{ fontSize: isMobile ? "14px" : "16px" }}>
          Cor Secundária
        </FormLabel>
        <Box
          sx={{
            display: "flex",
            gap: isMobile ? "6px" : "8px",
            flexDirection: isMobile ? "column" : "row",
            alignItems: isMobile ? "stretch" : "center",
          }}
        >
          <input
            type="color"
            value={secondaryColor}
            onChange={(e) => setSecondaryColor(e.target.value)}
            style={{
              width: isMobile ? "100%" : "50px",
              height: isMobile ? "48px" : "40px",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          />
          <TextField
            value={secondaryColor}
            onChange={(e) => setSecondaryColor(e.target.value)}
            placeholder="#dc004e"
            variant="outlined"
            disabled={loading}
            sx={{ flex: 1 }}
            size={isMobile ? "small" : "medium"}
          />
        </Box>
      </FormControl>

      <Button
        variant="contained"
        onClick={handleSaveBranding}
        disabled={loading || !primaryColor || !secondaryColor}
        fullWidth={isMobile}
        sx={{
          backgroundColor: colors.blue,
          "&:hover": {
            backgroundColor: colors.darkBlue,
          },
          fontWeight: 550,
          padding: isMobile ? "12px" : "8px 16px",
          fontSize: isMobile ? "16px" : "14px",
        }}
      >
        {loading ? (
          <CircularProgress size={24} color="inherit" />
        ) : (
          "Salvar Cores"
        )}
      </Button>
    </Box>
  );
};

export default BrandingStep;
